/**
 * Current user's permissions (cached), from get_current_user_permissions().
 * UI-level gating only — the database enforces the same permissions via RLS/RPCs.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/supabase.config';
import { useAuth } from '@/context/authContext';
import { grantsPermission, type PermissionGrant, type PermissionTarget } from '@/lib/permissions';

export type { PermissionGrant, PermissionScope, PermissionTarget } from '@/lib/permissions';

export const currentUserPermissionsKey = (userId: string | undefined) =>
  ['current_user_permissions', userId] as const;

export function usePermissions() {
  const { user } = useAuth();

  const { data: grants = [], isLoading, isError, refetch } = useQuery({
    queryKey: currentUserPermissionsKey(user?.id),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_current_user_permissions');
      if (error) throw new Error(error.message);
      return (data ?? []) as PermissionGrant[];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const codes = useMemo(() => new Set(grants.map((g) => g.code)), [grants]);
  const can = useCallback(
    (code: string, target?: PermissionTarget) =>
      target ? grantsPermission(grants, code, target) : codes.has(code),
    [grants, codes],
  );
  const canAny = useCallback((...list: string[]) => list.some((c) => codes.has(c)), [codes]);

  return { can, canAny, grants, isLoading, isError, refetch };
}
